import { Product } from "@bmi/elasticsearch-types";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import isEqual from "lodash-es/isEqual";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useConfig } from "../../contexts/ConfigProvider";
import { ESResponse } from "../../types/elasticsearch";
import { devLog } from "../../utils/devLog";
import { queryElasticSearch } from "../../utils/elasticSearch";
import { shallowEqual } from "../../utils/isObjectEqual";
import { useSiteContext } from "../Site";
import TileCategory, {
  TilesGroupedByMaterial,
  getTileMaterials
} from "./TileMaterial";
import Guttering, { GutteringSelections } from "./_Guttering";
import Results from "./_Results";
import RoofDimensions from "./_RoofDimensions";
import RoofSelection from "./_RoofSelection";
import TileOptions from "./_TileOptions";
import TileSelection from "./_TileSelection";
import UnderlaySelection from "./_UnderlaySelection";
import VariantSelection from "./_VariantSelection";
import ResultsHeader from "./subcomponents/results-header";
import { calculateArea } from "./calculation/calculate";
import protrusionTypes from "./calculation/protrusions";
import roofs from "./calculation/roofs";
import { AnalyticsContext } from "./helpers/analytics";
import { getProductsQuery } from "./helpers/esQueries";
import getPitchValues from "./helpers/getPitchValues";
import { prepareProducts } from "./helpers/products";
import CalculatorStepper from "./subcomponents/calculator-stepper/CalculatorStepper";
import {
  CalculatorConfig,
  CalculatorSteps,
  Data,
  GutteringFormSelection,
  type TileOptionSelections
} from "./types";
import {
  DimensionsValues,
  LinesMap,
  Measurements,
  Protrusion,
  RoofV2 as Roof
} from "./types/roof";
import getNestedProducts from "./helpers/getNestedProducts";
import fetchRidgeAccessories from "./helpers/fetchRidgeAccessories";
import useUnderlaySelection from "./hooks/useUnderlaySelection";
import useResultsStep from "./hooks/useResultsStep";
import useVariantSelection from "./hooks/useVariantSelection";
import useTileOptionsSelection from "./hooks/useTileOptionsSelection";

export type PitchedRoofCalculatorStepsProps = {
  isDebugging?: boolean;
  selected: CalculatorSteps;
  setSelected: (value: CalculatorSteps) => void;
  calculatorConfig: CalculatorConfig | null;
};

const PitchedRoofCalculatorSteps = ({
  isDebugging,
  selected,
  setSelected,
  calculatorConfig
}: PitchedRoofCalculatorStepsProps) => {
  const { getMicroCopy } = useSiteContext();
  const pushEvent = useContext(AnalyticsContext);

  const [roof, setRoof] = useState<Roof | undefined>(undefined);
  const [dimensions, setDimensions] = useState<DimensionsValues>({});
  const [tilesGroupedByMaterial, setTilesGroupedByMaterial] = useState<
    TilesGroupedByMaterial | undefined
  >(undefined);
  const [selectedMaterial, setSelectedMaterial] = useState<string | undefined>(
    undefined
  );
  const [mainTileCode, setMainTileCode] = useState<string | undefined>(
    undefined
  );
  const [tileOptions, setTileOptions] = useState<
    TileOptionSelections | undefined
  >(undefined);
  const [guttering, setGuttering] = useState<GutteringSelections | undefined>(
    undefined
  );
  const [isHubSpotFormAvailable, setIsHubSpotFormAvailable] = useState<boolean>(
    Boolean(calculatorConfig?.hubSpotFormId)
  );

  const [data, setData] = useState<Data>({
    tiles: {},
    underlays: [],
    gutterHooks: [],
    gutters: {}
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState<boolean>(false);
  const { esIndexNameProduct } = useConfig();

  const { selectVariant, variant, setVariant } = useVariantSelection({
    setSelectedStep: setSelected,
    mainTileCode,
    tiles: data.tiles,
    setTileOptions,
    setLoading
  });

  const { selectTileOptions } = useTileOptionsSelection({
    gutters: data.gutters,
    setSelectedStep: setSelected,
    variant,
    setTileOptions
  });

  const { handleUnderlaySelection, underlay, setUnderlay } =
    useUnderlaySelection({
      data,
      setSelectedStep: setSelected,
      tileOptions,
      setTileOptions,
      esIndexNameProduct
    });

  const { resultsGoBackHandler, resultsResetSelectionHandler } = useResultsStep(
    {
      setSelectedStep: setSelected,
      data,
      setGuttering,
      setRoof,
      setMainTileCode,
      setSelectedMaterial,
      setTileOptions,
      setUnderlay,
      setDimensions,
      setVariant,
      variant
    }
  );

  const fetchProducts = async (tileMaterial: string) => {
    setLoading(true);
    try {
      const pitchValues = getPitchValues(dimensions);
      const res: ESResponse<Product> = await queryElasticSearch(
        getProductsQuery(pitchValues, tileMaterial),
        esIndexNameProduct
      );
      const hits = res.hits.hits.map((hit) => hit._source);
      setData(prepareProducts(hits));
    } catch (err) {
      devLog("Failed to fetch data", err);
    }
    setLoading(false);
  };

  const selectRoof = (
    e: React.FormEvent,
    { roof: newRoofId }: { roof: string }
  ) => {
    e.preventDefault();
    const newRoof = roofs.find((roof) => roof.id === newRoofId);
    setSelected(CalculatorSteps.EnterDimensions);
    if (newRoof === roof) {
      return;
    }
    setRoof(newRoof);
    setDimensions({});
    pushEvent({
      event: "dxb.button_click",
      id: "rc-roof-type",
      label: getMicroCopy(microCopy.ROOF_SELECTION_NEXT_LABEL),
      action: "selected"
    });
  };

  const saveDimensions = async (
    e: React.FormEvent<Element>,
    values: DimensionsValues
  ) => {
    e.preventDefault();

    pushEvent({
      event: "dxb.button_click",
      id: "rc-dimensions",
      label: getMicroCopy(microCopy.ROOF_DIMENSIONS_NEXT_LABEL),
      action: "selected"
    });

    if (!isEqual(values, dimensions)) {
      setDimensions(values);
      setLoading(true);
      const tilesGroupedByCategories = await getTileMaterials(
        getPitchValues(values)
      );
      setTilesGroupedByMaterial(tilesGroupedByCategories);
      setLoading(false);
    }

    setSelectedMaterial(undefined);
    setSelected(CalculatorSteps.SelectTileCategory);
  };

  const onSelectMaterial = async (
    _event: React.FormEvent<Element>,
    { tileMaterial }: Record<string, unknown>
  ) => {
    //onSubmit prop of Form component is always being called with Record<string, unknown>. As a result 'tileMaterial' is unknown
    setSelectedMaterial(tileMaterial as string);
    setSelected(CalculatorSteps.SelectTile);
    setMainTileCode(undefined);
    await fetchProducts(tileMaterial as string);
  };

  const selectTile = (
    _: React.FormEvent<Element>,
    { tile: newTileCode }: { tile: string }
  ) => {
    setSelected(CalculatorSteps.SelectVariant);
    if (newTileCode === mainTileCode) {
      return;
    }
    setMainTileCode(newTileCode);
    setVariant(undefined);
    pushEvent({
      event: "dxb.button_click",
      id: "rc-select-tile",
      label: getMicroCopy(microCopy.TILE_SELECTION_NEXT_LABEL),
      action: "selected"
    });
  };

  const measurements: Measurements | null = useMemo(() => {
    if (!roof || !dimensions) {
      return null;
    }
    const { faces, lines } = roof.getMeasurements(dimensions);

    if (Array.isArray(dimensions.protrusions)) {
      for (const { type, ...protrusionDimensions } of dimensions.protrusions) {
        // eslint-disable-next-line security/detect-object-injection
        const protrusionType: Protrusion | undefined = protrusionTypes[type];
        if (protrusionType?.getMeasurements) {
          // eslint-disable-next-line security/detect-object-injection
          const { faces: pFaces, lines: pLines } =
            protrusionType.getMeasurements({
              ...protrusionDimensions,
              roofPitch: dimensions[roof.roofPitchField] // Says on what face of the roof the protrusion is
            });
          faces.push(...pFaces);
          Object.keys(lines).forEach((line) =>
            // eslint-disable-next-line security/detect-object-injection
            lines[line as keyof LinesMap].push(
              ...pLines[line as keyof LinesMap]
            )
          );
        }
      }
    }

    return {
      faces,
      lines,
      area: calculateArea(faces)
    };
  }, [roof, dimensions]);

  const formattedArea = ((measurements?.area || 0) / 10000).toFixed(2);

  useEffect(() => {
    if (selected === "your-solution-contains") {
      pushEvent({
        event: "dxb.button_click",
        id: "rc-solution-m2",
        label: "m²",
        action: formattedArea
      });
    }
  }, [selected, formattedArea, pushEvent]);

  const onGutterSkip = async () => {
    pushEvent({
      event: "dxb.button_click",
      id: "rc-select-guttering",
      label: getMicroCopy(microCopy.GUTTERING_SKIP_LABEL),
      action: "selected"
    });

    await fetchRidgeAccessories({
      tileOptions,
      setTileOptions,
      esIndexNameProduct
    });

    setGuttering(undefined);
    setSelected(CalculatorSteps.YourSolutionContains);
  };

  const onGutterSelect = async (
    e: React.FormEvent,
    selection: GutteringFormSelection
  ) => {
    e.preventDefault();
    const currentGuttering: GutteringFormSelection = {
      downPipeConnectors: guttering?.downPipeConnectors,
      downPipes: guttering?.downPipes,
      guttering: guttering?.guttering,
      gutteringHook: guttering?.gutteringHook?.externalProductCode,
      gutteringVariant: guttering?.gutteringVariant?.externalProductCode
    };

    if (shallowEqual(currentGuttering, selection)) {
      setSelected(CalculatorSteps.YourSolutionContains);
      return;
    }

    const ridgeReferencesToFetch = tileOptions?.ridge?.productReferences || [];
    const gutteringVariant = data.gutters[selection?.guttering]?.find(
      ({ externalProductCode }) =>
        externalProductCode === selection.gutteringVariant
    );
    const gutteringHook = data.gutterHooks.find(
      ({ externalProductCode }) =>
        selection?.gutteringHook === externalProductCode
    );

    const referencesToFetch = [
      ...ridgeReferencesToFetch,
      ...(gutteringVariant?.productReferences || [])
    ];

    try {
      if (!referencesToFetch.length) {
        setSelected(CalculatorSteps.YourSolutionContains);
        return;
      }

      const nestedProducts = await getNestedProducts(
        referencesToFetch,
        esIndexNameProduct
      );

      if (!nestedProducts) {
        setSelected(CalculatorSteps.YourSolutionContains);
        return;
      }

      const {
        downPipe,
        downPipeConnector,
        ridgeEnd,
        ridgeStart,
        yRidge,
        tRidge
      } = nestedProducts;

      if (tileOptions?.ridge) {
        setTileOptions({
          ...tileOptions,
          ridge: {
            ...tileOptions.ridge,
            ridgeEnd,
            ridgeStart,
            yRidge,
            tRidge
          }
        });
      }

      if (gutteringVariant) {
        setGuttering({
          downPipes: selection.downPipes,
          downPipeConnectors: selection.downPipeConnectors,
          gutteringHook,
          guttering: selection.guttering,
          gutteringVariant: { ...gutteringVariant, downPipe, downPipeConnector }
        });
      }
    } catch (err) {
      console.error("Failed to fetch inner products", err);
    }
    setSelected(CalculatorSteps.YourSolutionContains);
  };

  const handleGutterStepBackNavigation = () => {
    pushEvent({
      event: "dxb.button_click",
      id: "rc-select-guttering",
      label: getMicroCopy(microCopy.GUTTERING_BACK_LABEL),
      action: "selected"
    });

    if (variant?.requiresUnderlay) {
      setSelected(CalculatorSteps.SelectUnderlay);
      return;
    }

    setSelected(CalculatorSteps.TileOptions);
  };

  return (
    <div>
      <CalculatorStepper selected={selected} loading={loading}>
        <CalculatorStepper.Step
          key={CalculatorSteps.SelectRoof}
          title={getMicroCopy(microCopy.ROOF_SELECTION_TITLE)}
          subtitle={getMicroCopy(microCopy.ROOF_SELECTION_SUBTITLE)}
          nextLabel={getMicroCopy(microCopy.ROOF_SELECTION_NEXT_LABEL)}
          nextButtonOnClick={selectRoof}
        >
          <RoofSelection
            requiredRoofShapes={calculatorConfig?.roofShapes}
            isRequired
            defaultValue={roof?.id}
            name="roof"
            fieldIsRequiredError={getMicroCopy(
              microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
            )}
          />
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.EnterDimensions}
          title={getMicroCopy(microCopy.ROOF_DIMENSIONS_TITLE)}
          subtitle={getMicroCopy(microCopy.ROOF_DIMENSIONS_SUBTITLE)}
          nextLabel={getMicroCopy(microCopy.ROOF_DIMENSIONS_NEXT_LABEL)}
          nextButtonOnClick={saveDimensions}
          backLabel={getMicroCopy(microCopy.ROOF_DIMENSIONS_BACK_LABEL)}
          backButtonOnClick={() => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-dimensions",
              label: getMicroCopy(microCopy.ROOF_DIMENSIONS_BACK_LABEL),
              action: "selected"
            });
            setSelected(CalculatorSteps.SelectRoof);
          }}
        >
          {roof ? <RoofDimensions roof={roof} dimensions={dimensions} /> : null}
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.SelectTileCategory}
          title={getMicroCopy(microCopy.TILE_MATERIAL_SELECTION_TITLE)}
          subtitle={getMicroCopy(microCopy.TILE_MATERIAL_SELECTION_SUBTITLE)}
          backLabel={getMicroCopy(microCopy.TILE_MATERIAL_SELECTION_BACK_LABEL)}
          nextLabel={getMicroCopy(microCopy.TILE_MATERIAL_SELECTION_NEXT_LABEL)}
          backButtonOnClick={() => setSelected(CalculatorSteps.EnterDimensions)}
          nextButtonOnClick={onSelectMaterial}
          disableNextButton={!tilesGroupedByMaterial}
        >
          <TileCategory
            tilesGroupedByMaterial={tilesGroupedByMaterial}
            defaultValue={selectedMaterial}
            name="tileMaterial"
            isRequired
            fieldIsRequiredError={getMicroCopy(
              microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
            )}
          />
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.SelectTile}
          title={getMicroCopy(microCopy.TILE_SELECTION_TITLE)}
          subtitle={getMicroCopy(microCopy.TILE_SELECTION_SUBTITLE)}
          backLabel={getMicroCopy(microCopy.TILE_SELECTION_BACK_LABEL)}
          nextLabel={getMicroCopy(microCopy.TILE_SELECTION_NEXT_LABEL)}
          backButtonOnClick={() => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-select-tile",
              label: getMicroCopy(microCopy.TILE_SELECTION_BACK_LABEL),
              action: "selected"
            });
            setSelected(CalculatorSteps.SelectTileCategory);
            setMainTileCode(undefined);
          }}
          nextButtonOnClick={selectTile}
        >
          <TileSelection
            name="tile"
            isRequired
            fieldIsRequiredError={getMicroCopy(
              microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
            )}
            tiles={data.tiles}
            tileMaterial={selectedMaterial}
            defaultValue={mainTileCode}
          />
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.SelectVariant}
          title={getMicroCopy(microCopy.VARIANT_SELECTION_TITLE)}
          subtitle={getMicroCopy(microCopy.VARIANT_SELECTION_SUBTITLE)}
          backLabel={getMicroCopy(microCopy.VARIANT_SELECTION_BACK_LABEL)}
          nextLabel={getMicroCopy(microCopy.VARIANT_SELECTION_NEXT_LABEL)}
          backButtonOnClick={() => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-select-tile-colour",
              label: getMicroCopy(microCopy.VARIANT_SELECTION_BACK_LABEL),
              action: "selected"
            });
            setSelected(CalculatorSteps.SelectTile);
          }}
          nextButtonOnClick={selectVariant}
        >
          {mainTileCode ? (
            <VariantSelection
              selected={variant}
              // eslint-disable-next-line security/detect-object-injection
              options={data.tiles[mainTileCode]}
            />
          ) : null}
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.TileOptions}
          title={getMicroCopy(microCopy.TILE_OPTIONS_TITLE)}
          subtitle={getMicroCopy(microCopy.TILE_OPTIONS_SUBTITLE)}
          nextLabel={getMicroCopy(microCopy.TILE_OPTIONS_NEXT_LABEL)}
          nextButtonOnClick={async (e, values) => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-options-accessories",
              label: getMicroCopy(microCopy.TILE_OPTIONS_NEXT_LABEL),
              action: "selected"
            });
            await selectTileOptions(e, values);
          }}
          backLabel={getMicroCopy(microCopy.TILE_OPTIONS_BACK_LABEL)}
          backButtonOnClick={() => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-options-accessories",
              label: getMicroCopy(microCopy.TILE_OPTIONS_BACK_LABEL),
              action: "selected"
            });
            setSelected(CalculatorSteps.SelectVariant);
          }}
        >
          {variant ? (
            <TileOptions variant={variant} selections={tileOptions} />
          ) : null}
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.SelectUnderlay}
          title={getMicroCopy(microCopy.UNDERLAY_SELECTION_TITLE)}
          subtitle={getMicroCopy(microCopy.UNDERLAY_SELECTION_SUBTITLE)}
          nextLabel={getMicroCopy(microCopy.UNDERLAY_SELECTION_NEXT_LABEL)}
          nextButtonOnClick={handleUnderlaySelection}
          backLabel={getMicroCopy(microCopy.UNDERLAY_SELECTION_BACK_LABEL)}
          backButtonOnClick={() => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-select-underlay",
              label: getMicroCopy(microCopy.UNDERLAY_SELECTION_BACK_LABEL),
              action: "selected"
            });
            setSelected(CalculatorSteps.TileOptions);
          }}
        >
          <UnderlaySelection options={data.underlays} selected={underlay} />
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          key={CalculatorSteps.Guttering}
          title={getMicroCopy(microCopy.GUTTERING_TITLE)}
          subtitle={getMicroCopy(microCopy.GUTTERING_SUBTITLE)}
          nextLabel={getMicroCopy(microCopy.GUTTERING_NEXT_LABEL)}
          nextButtonOnClick={async (e, values) => {
            pushEvent({
              event: "dxb.button_click",
              id: "rc-select-guttering",
              label: getMicroCopy(microCopy.GUTTERING_NEXT_LABEL),
              action: "selected"
            });
            await onGutterSelect(e, values);
          }}
          backLabel={getMicroCopy(microCopy.GUTTERING_BACK_LABEL)}
          backButtonOnClick={handleGutterStepBackNavigation}
          linkLabel={getMicroCopy(microCopy.GUTTERING_SKIP_LABEL)}
          linkOnClick={onGutterSkip}
        >
          <Guttering
            selections={guttering}
            gutters={data.gutters}
            gutterHooks={data.gutterHooks}
          />
        </CalculatorStepper.Step>
        <CalculatorStepper.Step
          isForm={false}
          key={CalculatorSteps.YourSolutionContains}
          title={getMicroCopy(microCopy.RESULTS_TITLE)}
          subtitle={
            <ResultsHeader
              isHubSpotFormAvailable={isHubSpotFormAvailable}
              roof={roof!}
              dimensions={dimensions}
            />
          }
          paragraph={
            <span>
              {`${getMicroCopy(
                microCopy.RESULTS_AREA_LABEL
              )}: ${formattedArea}m`}
              <sup>2</sup>
            </span>
          }
          backLabel={getMicroCopy(microCopy.RESULTS_BACK_LABEL)}
          backButtonOnClick={resultsGoBackHandler}
          linkLabel={getMicroCopy(microCopy.RESULTS_START_OVER_LABEL)}
          linkOnClick={resultsResetSelectionHandler}
        >
          <Results
            isDebugging={isDebugging}
            measurements={measurements!}
            variant={variant!}
            tileOptions={tileOptions}
            underlay={underlay}
            guttering={guttering}
            hubSpotFormId={calculatorConfig?.hubSpotFormId}
            setIsHubSpotFormAvailable={setIsHubSpotFormAvailable}
            isHubSpotFormAvailable={isHubSpotFormAvailable}
            needHelpSection={calculatorConfig?.needHelpSection}
            roof={roof!}
            dimensions={dimensions}
          />
        </CalculatorStepper.Step>
      </CalculatorStepper>
    </div>
  );
};

export default PitchedRoofCalculatorSteps;
